import * as R from 'ramda';

export default function FindingCategoryController(
  $q,
  $rootScope,
  $scope,
  alerts,
  amDataService,
  amSharedModelService,
  confirmDialog,
  editFindingCategoryDialog,
  editFindingDialog,
  loaderService,
  orgDataService,
  ssDialogService,
) {
  const vm = this;

  // Set Default Values
  vm.canExpand = true;
  vm.isExpanded = false;

  vm.canEditItem = vm.findingCategory.canEdit && vm.auditIsActive;
  vm.canDeleteItem = vm.findingCategory.canDelete && vm.auditIsActive;

  // Attach Event Handlers
  vm.handleExpandFindingCategory = _handleExpandFindingCategory;
  vm.handleAddFinding = _handleAddFinding;
  vm.handleEditFindingCategory = _handleEditFindingCategory;
  vm.handleDeleteFindingCategory = _handleDeleteFindingCategory;
  vm.handleConfirmDelete = _handleConfirmDelete;
  vm.handleCancelDelete = _handleCancelDelete;
  vm.handleDeletedFinding = _handleDeletedFinding;

  _initEventListeners();
  _activate();

  /** **********************************************
   * Event Handlers
   */

  function _handleExpandFindingCategory() {
    if (!vm.isExpanded) {
      _loadFindings().then(() => {
        vm.isExpanded = true;
      });
    } else {
      vm.isExpanded = false;
    }
  }

  function _handleAddFinding($event) {
    return amDataService.getDefaultFinding(vm.findingCategory.findingCategoryId).then((response) => {
      ssDialogService.dialogs.auditTrail.editFindingDialog = ssDialogService.alert(
        editFindingDialog.getOptions($event, {
          customerId: vm.customerId,
          auditId: vm.auditId,
          businessRisks: vm.businessRisks,
          technicalRisks: vm.technicalRisks,
          priorities: vm.priorities,
          statuses: vm.statuses,
          users: vm.users,
          finding: response.data,
          addCallback: _handleAddedFinding,
        }),
      );
      return ssDialogService.show(ssDialogService.dialogs.auditTrail.editFindingDialog);
    });
  }

  function _handleEditFindingCategory($event) {
    return amDataService.getFindingCategory(vm.findingCategory.findingCategoryId).then(() => {
      ssDialogService.dialogs.auditTrail.editFindingCategoryDialog = ssDialogService.alert(
        editFindingCategoryDialog.getOptions($event, {
          findingCategory: vm.findingCategory,
        }),
      );
      return ssDialogService.show(ssDialogService.dialogs.auditTrail.editFindingCategoryDialog);
    });
  }

  function _handleDeleteFindingCategory($event) {
    ssDialogService.dialogs.auditTrail.deleteFindingCategoryConfirmDialog = ssDialogService.alert(
      confirmDialog.getOptions($event, {
        dialogTitle: 'Delete Finding Category?',
        message: `Are you sure you want to delete Finding Category "${vm.findingCategory.name}"?`,
        handleYesClick: _handleConfirmDelete,
        handleNoClick: _handleCancelDelete,
      }),
    );
    return ssDialogService.show(ssDialogService.dialogs.auditTrail.deleteFindingCategoryConfirmDialog);
  }

  function _handleConfirmDelete() {
    loaderService.showViewLoader = true;
    return amDataService.deleteFindingCategory(vm.findingCategory.findingCategoryId)
      .then(() => {
        alerts.success('The Finding Category has been deleted successfully.');
        $rootScope.$broadcast('refreshAMFindingCategories', { auditId: vm.auditId });
        _closeDeleteDialog();
      }).catch(() => {
        alerts.error('Error occurred while deleting the Finding Category.');
      }).finally(() => {
        loaderService.showViewLoader = false;
      });
  }

  function _handleCancelDelete() {
    _closeDeleteDialog();
  }

  function _handleDeletedFinding(finding) {
    if (!finding) return;
    const pos = vm.findings.findIndex(item => item.findingId === finding.findingId);
    if (pos === -1) return; // Can't find the item
    vm.findings.splice(pos, 1);
  }

  /** **********************************************
   * Private Functions
   */

  function _activate() {
    const dfdActivate = $q.defer();

    _initEventListeners();
    _refreshBusinessRisks().then(
      _refreshTechnicalRisks().then(
        _refreshPriorities().then(
          _refreshFindingStatuses().then(
            _refreshUsers().then(
              dfdActivate.resolve,
            ),
          ),
        ),
      ),
    );
  }

  function _initEventListeners() {
    $scope.$on('refreshAMBusinessRisks', _refreshBusinessRisks);
    $scope.$on('refreshAMTechnicalRisks', _refreshTechnicalRisks);
    $scope.$on('refreshAMPriorities', _refreshPriorities);
    $scope.$on('refreshAMUsers', _refreshUsers);

    $scope.$on('refreshAMFindings', (event, opt) => {
      if (opt.findingCategoryId === vm.findingCategory.findingCategoryId) {
        _loadFindings();
      }
    });

    $scope.$on('refreshAMFindingCategory', (event, opt) => {
      if (opt.findingCategoryId === vm.findingCategory.findingCategoryId) {
        _refreshFindingCategory();
        _loadFindings();
      }
    });
  }

  function _handleAddedFinding(finding) {
    vm.findings = vm.findings || [];
    vm.findings.push(finding);
    if (vm.findings.length > 1) {
      vm.findings = amSharedModelService.sortByName(vm.findings);
    }
    _setFindingAttributes();
  }

  function _refreshFindingCategory() {
    return amDataService.getFindingCategory(vm.findingCategory.findingCategoryId).then((response) => {
      vm.findingCategory = response.data;
    });
  }

  function _loadFindings() {
    loaderService.showViewLoader = true;
    return amSharedModelService.getFindings(vm.findingCategory.findingCategoryId).then((findings) => {
        loaderService.showViewLoader = false;
        vm.findings = findings;
        _setFindingAttributes();
      });
  }

  function _setFindingAttributes() {
    R.forEach((finding) => {
      finding.canAddItemNote = (finding.canAddNote || finding.isAssignedToMe) && vm.auditIsActive;
      finding.canAttachItemItems = (finding.canAttachItems || finding.isAssignedToMe) && vm.auditIsActive;

      finding.canChangeItemStatus = (finding.canChangeStatus || finding.isAssignedToMe || finding.canEdit)
        && finding.statusId !== 3 && vm.auditIsActive;

      finding.canEditItem = finding.canEdit && vm.auditIsActive;
      finding.canDeleteItem = finding.canDelete && vm.auditIsActive;
    }, vm.findings);
  }

  function _refreshBusinessRisks() {
    return amSharedModelService.getBusinessRisks().then((businessRisks) => {
      vm.businessRisks = businessRisks;
    });
  }

  function _refreshTechnicalRisks() {
    const dfdRefresh = $q.defer();

    vm.technicalRisks = amSharedModelService.technicalRisks;
    dfdRefresh.resolve();

    return dfdRefresh.promise;
  }

  function _refreshPriorities() {
    const dfdRefresh = $q.defer();

    vm.priorities = amSharedModelService.priorities;
    dfdRefresh.resolve();

    return dfdRefresh.promise;
  }

  function _refreshUsers() {
    const dfdRefresh = $q.defer();

    vm.users = amSharedModelService.applicationUsers;
    dfdRefresh.resolve();

    return dfdRefresh.promise;
  }

  function _refreshFindingStatuses() {
    const dfdRefresh = $q.defer();

    vm.statuses = amSharedModelService.findingStatuses;
    dfdRefresh.resolve();

    return dfdRefresh.promise;
  }

  function _closeDeleteDialog() {
    return ssDialogService.hide(ssDialogService.dialogs.auditTrail.deleteFindingCategoryConfirmDialog);
  }
}

FindingCategoryController.$inject = [
  '$q',
  '$rootScope',
  '$scope',
  'alerts',
  'amDataService',
  'amSharedModelService',
  'confirmDialog',
  'editFindingCategoryDialog',
  'editFindingDialog',
  'loaderService',
  'orgDataService',
  'ssDialogService',
];
